import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, ShieldCheck, Database, Smartphone, Laptop, ArrowRight, ExternalLink, CheckCircle2, Stethoscope, HeartPulse } from 'lucide-react';
import Button from '../ui/Button';

const FeaturedProduct = () => {
  const [activeTab, setActiveTab] = useState('web');

  return (
    <section className="py-24 relative bg-slate-50/70 dark:bg-slate-900/60 border-t border-slate-200/80 dark:border-slate-800 overflow-hidden">
      <div className="container mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="max-w-3xl mb-12">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200/80 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 text-xs font-semibold uppercase tracking-wider mb-4">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            Featured Technology Platform
          </div>
          <h2 className="text-3xl md:text-5xl font-display font-bold text-slate-900 dark:text-white tracking-tight mb-4">
            VaidyaMed-X
          </h2>
          <p className="text-lg md:text-xl text-slate-600 dark:text-slate-300 leading-relaxed font-normal">
            An intelligent healthcare technology platform designed to connect patients, doctors and digital healthcare services through verified AI triage and secure records.
          </p>
        </div>

        {/* Dual Layout: Interactive Previews & Feature Highlights */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          
          {/* Left Column: Interactive Application Previews */}
          <div className="lg:col-span-7">
            {/* View Switcher Controls */}
            <div className="flex items-center gap-2 mb-4 bg-white dark:bg-slate-800 p-1.5 rounded-2xl border border-slate-200 dark:border-slate-700 inline-flex shadow-xs">
              <button
                onClick={() => setActiveTab('web')}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
                  activeTab === 'web'
                    ? 'bg-brand-600 text-white shadow-xs'
                    : 'text-slate-600 dark:text-slate-300 hover:text-slate-900'
                }`}
              >
                <Laptop size={15} />
                Web Application (Clinical Console)
              </button>
              <button
                onClick={() => setActiveTab('mobile')}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
                  activeTab === 'mobile'
                    ? 'bg-brand-600 text-white shadow-xs'
                    : 'text-slate-600 dark:text-slate-300 hover:text-slate-900'
                }`}
              >
                <Smartphone size={15} />
                Mobile Application (Patient Portal)
              </button>
            </div>

            {/* Container for Preview */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-card overflow-hidden min-h-[380px] flex flex-col justify-between">
              
              {activeTab === 'web' ? (
                /* Web Clinical Dashboard Preview */
                <div>
                  {/* Browser Bar */}
                  <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3 mb-5">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full bg-slate-300 dark:bg-slate-700" />
                      <div className="w-2.5 h-2.5 rounded-full bg-slate-300 dark:bg-slate-700" />
                      <div className="w-2.5 h-2.5 rounded-full bg-slate-300 dark:bg-slate-700" />
                      <span className="ml-2 font-mono text-xs text-slate-400">
                        https://vaidyamedx.in/clinical-console
                      </span>
                    </div>
                    <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                      <Activity size={12} /> Live Engine
                    </span>
                  </div>

                  {/* Clinical Interface Mockup */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                    <div className="sm:col-span-1 p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/60 dark:border-slate-700/60 space-y-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Patient Queue</p>
                      <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200/50 text-xs">
                        <p className="font-semibold text-slate-800 dark:text-slate-200">Patient #2049</p>
                        <p className="text-[11px] text-emerald-700 dark:text-emerald-300">Triage: High Priority</p>
                      </div>
                      <div className="p-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200/40 text-xs opacity-70">
                        <p className="font-medium text-slate-700 dark:text-slate-300">Patient #2050</p>
                        <p className="text-[11px] text-slate-400">Triage: Routine</p>
                      </div>
                    </div>

                    <div className="sm:col-span-2 p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/60 dark:border-slate-700/60 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                          <Stethoscope size={13} className="text-brand-600" /> AI Differential Assessment
                        </span>
                        <span className="text-[10px] font-mono bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300 px-2 py-0.5 rounded">
                          FAISS RAG: Indexed
                        </span>
                      </div>
                      <div className="p-2.5 rounded-lg bg-white dark:bg-slate-800 border border-slate-200/60 text-xs space-y-1">
                        <p className="font-semibold text-slate-800 dark:text-slate-100">Primary Symptom Vector</p>
                        <p className="text-slate-500 text-[11px]">Acute seasonal respiratory discomfort with elevated body temperature.</p>
                        <div className="pt-1.5 flex items-center gap-2 text-[10px] text-emerald-600 font-medium">
                          <span>✓ Verified by 3 clinical papers</span>
                          <span>•</span>
                          <span>Non-emergency preventative regimen suggested</span>
                        </div>
                      </div>
                      <div className="flex gap-2 text-[11px]">
                        <span className="px-2.5 py-1 bg-slate-200 dark:bg-slate-700 rounded text-slate-700 dark:text-slate-300 font-medium">
                          Generate Prescription
                        </span>
                        <span className="px-2.5 py-1 bg-emerald-600 text-white rounded font-medium">
                          Approve Care Plan
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 rounded-xl bg-slate-100/70 dark:bg-slate-800/40 border border-slate-200/50 flex items-center justify-between text-xs text-slate-600 dark:text-slate-400">
                    <span className="flex items-center gap-1.5 font-mono">
                      <ShieldCheck size={14} className="text-emerald-600" /> Zero-Knowledge Patient Privacy (HIPAA architecture aligned)
                    </span>
                    <span className="font-mono text-[11px]">v2.4.0 Production</span>
                  </div>
                </div>
              ) : (
                /* Mobile App Preview */
                <div className="flex flex-col sm:flex-row gap-6 items-center justify-center py-2">
                  <div className="w-64 bg-slate-900 border-4 border-slate-800 rounded-3xl p-3 shadow-2xl text-white">
                    {/* Phone Notch */}
                    <div className="w-20 h-3 bg-slate-800 rounded-full mx-auto mb-3" />
                    <div className="space-y-3 font-sans">
                      <div className="flex items-center justify-between text-[11px] text-slate-300">
                        <span className="font-semibold">VaidyaMed-X Mobile</span>
                        <span className="text-emerald-400">Online</span>
                      </div>
                      <div className="bg-slate-800/80 p-2.5 rounded-xl border border-slate-700">
                        <p className="text-[10px] text-slate-400">Wellness Score</p>
                        <p className="text-lg font-bold text-emerald-400">94 / 100</p>
                        <p className="text-[10px] text-slate-300 mt-0.5">Biometrics stable across 7 days</p>
                      </div>
                      <div className="bg-brand-600/20 border border-brand-500/40 p-2.5 rounded-xl">
                        <p className="text-[10px] font-semibold text-brand-300">Next Doctor Consultation</p>
                        <p className="text-xs font-bold text-white">Dr. S. Sharma (Cardio)</p>
                        <p className="text-[10px] text-slate-300">Today at 4:30 PM (Encrypted Video)</p>
                      </div>
                      <button className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 rounded-xl text-xs font-semibold text-white shadow-xs">
                        Start Symptom Check
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3 text-sm max-w-xs">
                    <h4 className="font-bold font-display text-slate-900 dark:text-white">Native Mobile Telehealth</h4>
                    <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                      Patients access seamless real-time consultations, track continuous vital metrics, and store verified Ayurvedic & allopathic recommendations securely on device.
                    </p>
                    <div className="space-y-1.5 pt-2">
                      <div className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300">
                        <CheckCircle2 size={14} className="text-emerald-600" /> Offline symptom reference models
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300">
                        <CheckCircle2 size={14} className="text-emerald-600" /> Biometric app lock security
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300">
                        <CheckCircle2 size={14} className="text-emerald-600" /> Multi-language voice translation
                      </div>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>

          {/* Right Column: Key Feature Highlights & CTA */}
          <div className="lg:col-span-5 space-y-6">
            <div className="space-y-4">
              <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-subtle">
                <div className="flex items-start gap-3.5">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800 flex items-center justify-center shrink-0">
                    <Database className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="font-bold font-display text-slate-900 dark:text-white text-base mb-1">
                      RAG-Based Clinical Retrieval
                    </h4>
                    <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                      Indexes verified medical textbooks, pharmacological cross-interactions, and traditional Ayurvedic knowledge using FAISS vector indexing.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-subtle">
                <div className="flex items-start gap-3.5">
                  <div className="w-10 h-10 rounded-xl bg-brand-50 dark:bg-brand-950/60 border border-brand-200 dark:border-brand-800 flex items-center justify-center shrink-0">
                    <ShieldCheck className="w-5 h-5 text-brand-600 dark:text-brand-400" />
                  </div>
                  <div>
                    <h4 className="font-bold font-display text-slate-900 dark:text-white text-base mb-1">
                      Patient Privacy & Encrypted Records
                    </h4>
                    <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                      Designed with zero-knowledge cryptography ensuring health data is accessible only by authorized patients and clinicians.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-subtle">
                <div className="flex items-start gap-3.5">
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-200 dark:border-indigo-800 flex items-center justify-center shrink-0">
                    <HeartPulse className="w-5 h-5 text-indigoBrand-600 dark:text-indigoBrand-400" />
                  </div>
                  <div>
                    <h4 className="font-bold font-display text-slate-900 dark:text-white text-base mb-1">
                      Connected Doctor-Patient Telehealth
                    </h4>
                    <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                      Low-bandwidth WebRTC video consults with real-time AI assistive transcription and structured diagnostic care plan generation.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="pt-2 flex flex-wrap items-center gap-4">
              <Link to="/projects/vaidyamed-x">
                <Button variant="primary" className="px-6 py-3 font-semibold shadow-xs">
                  Explore VaidyaMed-X
                  <ArrowRight size={16} />
                </Button>
              </Link>
              
              <a href="https://vaidyamedx.in" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="px-5 py-3 font-medium flex items-center gap-2">
                  Visit Live Portal <ExternalLink size={15} />
                </Button>
              </a>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default FeaturedProduct;
